import { EAppSupportType } from "../models/app-support-type";
import { EProjectType } from "../models/project-type";

/**
 * Where a component or property is about to be offered: the project's type and whether the screen being edited
 * is a WebScreen (a Mobile project's web-rendered screen, where web-only things are legitimately allowed).
 */
export interface IScreenTargetContext {
  readonly appSupportType: EAppSupportType | undefined | null;
  readonly projectType: EProjectType | undefined;
  readonly isWebScreen: boolean;
  /**
   * Whether this user, in this workspace, may generate mobile platforms with React Native (the
   * `RN_BETA_ALLOWLIST` gate). Decides whether `WebAndReactNative` is offered on a MOBILE screen: someone who
   * cannot produce a React Native build has no use for a component only React Native renders, so it is hidden
   * for them instead of badged. Callers that cannot know (server-side catalogs) omit it — absent means offered,
   * i.e. the union, and the backend validation stays the gate.
   */
  readonly reactNativeOffered?: boolean;
}

/**
 * The ONE platform predicate behind the designer's component palette, drop and paste guards, property panel,
 * child-target support and the MCP catalog. It used to be copied inline in five places, each spelling the same
 * two rules; a fourth `appSupportType` value made that a liability.
 *
 * Rules:
 *  - `All` (or no flag) is offered everywhere.
 *  - `Web` is offered on a Desktop project and on a Mobile project's WebScreens, never on a mobile screen.
 *  - `Mobile` is offered only on a Mobile project.
 *  - `WebAndReactNative` is offered on Desktop and on WebScreens like `All`. On a MOBILE screen it is offered
 *    when React Native generation is open to this user/workspace (`reactNativeOffered`, the RN_BETA_ALLOWLIST
 *    gate) — the UNION across the project's platforms, by decision: a Mobile project may generate one platform
 *    natively and still see the component, and the publish validation (KV1263) tells that platform the truth.
 *    When the gate is CLOSED the component is hidden there instead: nothing that user can build renders it.
 *    `isReactNativeOnlyOnScreen` says when to badge it.
 */
export function isAppSupportTypeOfferedOnScreen(context: IScreenTargetContext): boolean {
  const supportType: EAppSupportType = context.appSupportType ?? EAppSupportType.All;
  if (supportType === EAppSupportType.Web) {
    return context.projectType === EProjectType.Desktop || context.isWebScreen;
  }
  if (supportType === EAppSupportType.Mobile) {
    return context.projectType !== EProjectType.Desktop;
  }
  if (supportType === EAppSupportType.WebAndReactNative) {
    const isMobileScreen: boolean = context.projectType === EProjectType.Mobile && !context.isWebScreen;
    return !isMobileScreen || context.reactNativeOffered !== false;
  }
  return true;
}

/**
 * Whether this component renders on the screen being edited ONLY through the React Native generator — i.e. it
 * is `WebAndReactNative` and the screen is a Mobile project's mobile screen. The palette badges these so a user
 * on a Native configuration learns before dropping, not from the publish error after.
 */
export function isReactNativeOnlyOnScreen(context: IScreenTargetContext): boolean {
  return (
    context.appSupportType === EAppSupportType.WebAndReactNative &&
    context.projectType === EProjectType.Mobile &&
    !context.isWebScreen
  );
}
